import moment, {type Moment} from "moment-timezone";

import {SAFELISTING_COVERAGE_FORMATS as F} from "@/src/modules/core/lib/formatters";
import type {PropertyRuleTimeRange} from "@/src/modules/properties/types";

import {
  computeSafelistingCoveragePreview,
  getSafelistingAnchorCoverageEnd,
  getSafelistingCoverageWindowKind,
  hasSafelistingNightCoverageStarted,
  isSafelistingAnchorDateSelectable,
} from "./compute-safelisting-coverage-preview";
import type {DatesRange} from "../types";

function minutesFromHHMM(hhmm: string): number {
  const [h, m] = hhmm.split(":").map((part) => parseInt(part, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

/** Daytime window from midnight (e.g. 00:00–06:00): calendar date is “tomorrow” but hours are the middle of the night. */
function isEarlyMorningDaytimeWindow(rangeHours: PropertyRuleTimeRange): boolean {
  return (
    getSafelistingCoverageWindowKind(rangeHours) === "daytime" &&
    minutesFromHHMM(rangeHours.start) === 0
  );
}

export type CoverageAnchorMismatch = {
  likelyAnchor: string;
  selectedAnchor: string;
  likelyAnchorLabel: string;
  selectedAnchorLabel: string;
  likelyCoverageLine: string;
  selectedCoverageLine: string;
};

function getQuickPickLabelsWhenTodayEnded(
  rangeHours: PropertyRuleTimeRange,
): {
  current: string;
  next: string;
} {
  const kind = getSafelistingCoverageWindowKind(rangeHours);
  if (kind === "overnight") {
    return {current: "Tonight", next: "Tomorrow night"};
  }
  if (isEarlyMorningDaytimeWindow(rangeHours)) {
    return {current: "Tomorrow night", next: "Day after"};
  }
  return {current: "Tomorrow", next: "Day after"};
}

/** Short end-time label for in-progress copy (time only when end is today in property TZ). */
export function formatAnchorCoverageEndLabel(
  anchor: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): string {
  const end = getSafelistingAnchorCoverageEnd(anchor, rangeHours, timeZone);
  if (end.format("YYYY-MM-DD") === now.format("YYYY-MM-DD")) {
    return end.format(F.time);
  }
  return end.format(F.compactWithDate);
}

function getInProgressAnchorLabel(
  anchor: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): string {
  const endLabel = formatAnchorCoverageEndLabel(anchor, rangeHours, timeZone, now);
  return `Now until ${endLabel}`;
}

/**
 * Label for the calendar day after `likelyAnchor` (quick pick + breakdown rows).
 * During an overnight morning tail, that day is still "Tonight", not "Tomorrow night".
 */
function getNextAnchorLabel(
  likelyAnchor: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): string {
  const kind = getSafelistingCoverageWindowKind(rangeHours);
  const todayStr = now.format("YYYY-MM-DD");
  const inProgress = isAnchorCoverageInProgress(likelyAnchor, rangeHours, timeZone, now);

  if (inProgress && kind === "overnight" && likelyAnchor < todayStr) {
    return "Tonight";
  }

  if (kind === "overnight" || isEarlyMorningDaytimeWindow(rangeHours)) {
    return "Tomorrow night";
  }

  return "Tomorrow";
}

/** Coverage window has begun and the anchor is still selectable (partial permit remaining). */
export function isAnchorCoverageInProgress(
  anchor: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): boolean {
  return (
    isSafelistingAnchorDateSelectable(anchor, rangeHours, timeZone, now) &&
    hasSafelistingNightCoverageStarted(anchor, rangeHours, timeZone, now)
  );
}

/** Quick-pick labels — phase-aware when the likely anchor is already in progress. */
export function getCoverageAnchorQuickPickLabels(
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): {
  current: string;
  next: string;
} | null {
  const anchors = getSafelistingQuickPickAnchors(rangeHours, timeZone, now);
  if (!anchors) return null;

  const kind = getSafelistingCoverageWindowKind(rangeHours);
  const likelyAnchor = getLikelySafelistingAnchorForNow(rangeHours, timeZone, now);

  if (likelyAnchor && isAnchorCoverageInProgress(likelyAnchor, rangeHours, timeZone, now)) {
    return {
      current: getInProgressAnchorLabel(likelyAnchor, rangeHours, timeZone, now),
      next: getNextAnchorLabel(likelyAnchor, rangeHours, timeZone, now),
    };
  }

  if (likelyAnchor) {
    if (kind === "overnight") {
      return {current: "Tonight", next: "Tomorrow night"};
    }
    if (isEarlyMorningDaytimeWindow(rangeHours)) {
      return {current: "Tonight", next: "Tomorrow night"};
    }
    return {current: "Today", next: "Tomorrow"};
  }

  return getQuickPickLabelsWhenTodayEnded(rangeHours);
}

/**
 * Anchor date (YYYY-MM-DD) users usually mean for immediate / current coverage,
 * based on property range hours and property timezone.
 */
export function getLikelySafelistingAnchorForNow(
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): string | null {
  const kind = getSafelistingCoverageWindowKind(rangeHours);
  const today = now.format("YYYY-MM-DD");

  if (kind === "overnight") {
    const yesterday = now.clone().subtract(1, "day").format("YYYY-MM-DD");
    if (
      isSafelistingAnchorDateSelectable(yesterday, rangeHours, timeZone, now) &&
      hasSafelistingNightCoverageStarted(yesterday, rangeHours, timeZone, now)
    ) {
      return yesterday;
    }
  }

  if (isSafelistingAnchorDateSelectable(today, rangeHours, timeZone, now)) {
    return today;
  }

  return null;
}

/** First selectable anchor on or after tomorrow when today's window has ended. */
export function getNextSelectableSafelistingAnchorAfterToday(
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
  maxLookaheadDays = 60,
): string | null {
  const today = now.format("YYYY-MM-DD");
  let candidate = addCalendarDay(today, timeZone);

  for (let i = 0; i < maxLookaheadDays; i++) {
    if (isSafelistingAnchorDateSelectable(candidate, rangeHours, timeZone, now)) {
      return candidate;
    }
    candidate = addCalendarDay(candidate, timeZone);
  }

  return null;
}

/** Primary + secondary anchors for Tonight/Tomorrow quick picks (still shown when today ended). */
export function getSafelistingQuickPickAnchors(
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): {primary: string; secondary: string} | null {
  const primary =
    getLikelySafelistingAnchorForNow(rangeHours, timeZone, now) ??
    getNextSelectableSafelistingAnchorAfterToday(rangeHours, timeZone, now);

  if (!primary) return null;

  return {
    primary,
    secondary: addCalendarDay(primary, timeZone),
  };
}

export function addCalendarDay(anchor: string, timeZone: string, days = 1): string {
  return moment.tz(anchor, "YYYY-MM-DD", timeZone).add(days, "day").format("YYYY-MM-DD");
}

export function anchorStringToLocalDate(anchor: string): Date {
  return moment(anchor, "YYYY-MM-DD").startOf("day").toDate();
}

export function singleAnchorDatesRange(anchor: string): DatesRange {
  const date = anchorStringToLocalDate(anchor);
  return {from: date, to: date};
}

function coverageLineForAnchor(
  anchor: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
): string {
  const preview = computeSafelistingCoveragePreview(rangeHours, [anchor], timeZone);
  const row = preview?.rows[0];
  if (!row) return anchor;
  return `${row.compactDateLabel}, ${row.compactTimeLabel}`;
}

/** Human label for a row relative to the likely anchor (Tonight/Today, Until HH:mm, etc.). */
export function getCoverageAnchorHumanLabel(
  anchor: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): string | null {
  const quickPickAnchors = getSafelistingQuickPickAnchors(rangeHours, timeZone, now);
  if (!quickPickAnchors) return null;

  const {primary, secondary} = quickPickAnchors;
  const quickPickLabels = getCoverageAnchorQuickPickLabels(rangeHours, timeZone, now);

  if (anchor === primary) {
    if (isAnchorCoverageInProgress(primary, rangeHours, timeZone, now)) {
      return getInProgressAnchorLabel(primary, rangeHours, timeZone, now);
    }
    return quickPickLabels?.current ?? null;
  }

  if (anchor === secondary) {
    return quickPickLabels?.next ?? null;
  }

  return null;
}

/** Single-night preview headline after "Covers" (in-progress uses `now → end`). */
export function getSingleNightCoverageHeadline(
  anchor: string,
  rangeHours: PropertyRuleTimeRange,
  compactTimeLabel: string,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): string {
  if (isAnchorCoverageInProgress(anchor, rangeHours, timeZone, now)) {
    const endLabel = formatAnchorCoverageEndLabel(anchor, rangeHours, timeZone, now);
    return `now → ${endLabel}`;
  }
  const humanLabel = getCoverageAnchorHumanLabel(anchor, rangeHours, timeZone, now);
  if (humanLabel) return `${humanLabel} · ${compactTimeLabel}`;
  return compactTimeLabel;
}

export function detectCoverageAnchorMismatch(
  datesRange: DatesRange | undefined,
  rangeHours: PropertyRuleTimeRange | null | undefined,
  timeZone: string,
  now: Moment = moment.tz(timeZone),
): CoverageAnchorMismatch | null {
  if (!rangeHours) return null;

  const from = datesRange?.from;
  if (!from) return null;

  const quickPickAnchors = getSafelistingQuickPickAnchors(rangeHours, timeZone, now);
  if (!quickPickAnchors) return null;

  const {primary: likelyAnchor} = quickPickAnchors;
  const selectedAnchor = moment(from).format("YYYY-MM-DD");
  const expectedWrongPick = quickPickAnchors.secondary;
  if (selectedAnchor !== expectedWrongPick) return null;

  const likelyCoverageLine = coverageLineForAnchor(likelyAnchor, rangeHours, timeZone);
  const selectedCoverageLine = coverageLineForAnchor(selectedAnchor, rangeHours, timeZone);
  const quickPickLabels = getCoverageAnchorQuickPickLabels(rangeHours, timeZone, now);
  const likelyAnchorLabel = isAnchorCoverageInProgress(likelyAnchor, rangeHours, timeZone, now)
    ? getInProgressAnchorLabel(likelyAnchor, rangeHours, timeZone, now)
    : (quickPickLabels?.current ?? likelyAnchor);
  const selectedAnchorLabel =
    quickPickLabels?.next ?? getNextAnchorLabel(likelyAnchor, rangeHours, timeZone, now);

  return {
    likelyAnchor,
    selectedAnchor,
    likelyAnchorLabel,
    selectedAnchorLabel,
    likelyCoverageLine,
    selectedCoverageLine,
  };
}
